// ============================================================================
//  LeafletMap.tsx — Composant carte Leaflet réutilisable (React wrapper)
//  Utilise le global window.L chargé depuis CDN dans index.html
// ============================================================================
import { useRef, useEffect, useCallback } from 'react'

declare global {
  interface Window { L: any }
}

export interface MapMarker {
  id: string
  lat: number
  lng: number
  label?: string
  popup?: string
  color?: string
  pulse?: boolean
}

export interface MapCircle {
  id: string
  lat: number
  lng: number
  radius: number
  color?: string
  fillColor?: string
  label?: string
  popup?: string
}

export interface MapPolygon {
  id: string
  points: { lat: number; lng: number }[]
  color?: string
  fillColor?: string
  label?: string
  popup?: string
}

export interface HeatPoint {
  lat: number
  lng: number
  intensity?: number
}

interface LeafletMapProps {
  height?: number | string
  center?: [number, number]
  zoom?: number
  markers?: MapMarker[]
  circles?: MapCircle[]
  polygons?: MapPolygon[]
  heatPoints?: HeatPoint[]
  polyline?: { lat: number; lng: number }[]
  polylineColor?: string
  fitBounds?: boolean
  className?: string
  onMapClick?: (lat: number, lng: number) => void
}

const COLORS = {
  work_zone: 'var(--brand-orange)',
  office: 'var(--brand-amber)',
  warehouse: '#f59e0b',
  forbidden: '#ef4444',
  default: 'var(--brand-orange)',
}

function createPulseIcon(L: any, color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="position:relative;width:24px;height:24px">
      <div style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:.3;animation:pulse-ring 2s ease-out infinite"></div>
      <div style="position:absolute;top:4px;left:4px;width:16px;height:16px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>
    </div>
    <style>@keyframes pulse-ring{0%{transform:scale(1);opacity:.4}100%{transform:scale(2.2);opacity:0}}</style>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
  })
}

function createDotIcon(L: any, color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  })
}

export default function LeafletMap({
  height = 450,
  center = [14.6937, -17.4441],
  zoom = 13,
  markers = [],
  circles = [],
  polygons = [],
  heatPoints = [],
  polyline,
  polylineColor = 'var(--brand-orange)',
  fitBounds = true,
  className = '',
  onMapClick,
}: LeafletMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const layersRef = useRef<any[]>([])

  const clearLayers = useCallback(() => {
    layersRef.current.forEach(l => {
      try { mapRef.current?.removeLayer(l) } catch {}
    })
    layersRef.current = []
  }, [])

  // Initialize map
  useEffect(() => {
    const L = window.L
    if (!L || !containerRef.current || mapRef.current) return

    const map = L.map(containerRef.current, {
      center,
      zoom,
      zoomControl: true,
      attributionControl: false,
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map)

    L.control.attribution({ prefix: false }).addTo(map)

    mapRef.current = map

    if (onMapClick) {
      map.on('click', (e: any) => onMapClick(e.latlng.lat, e.latlng.lng))
    }

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [])

  // Update layers
  useEffect(() => {
    const L = window.L
    const map = mapRef.current
    if (!L || !map) return

    clearLayers()
    const bounds: [number, number][] = []

    // Markers — skip any with invalid coordinates
    markers.forEach(m => {
      if (!Number.isFinite(m.lat) || !Number.isFinite(m.lng)) return
      const icon = m.pulse
        ? createPulseIcon(L, m.color || COLORS.default)
        : createDotIcon(L, m.color || COLORS.default)
      const marker = L.marker([m.lat, m.lng], { icon }).addTo(map)
      if (m.popup || m.label) {
        marker.bindPopup(`<div style="font-family:Raleway,sans-serif;font-size:13px">${m.popup || m.label}</div>`)
      }
      if (m.label) {
        marker.bindTooltip(m.label, { direction: 'top', offset: [0, -12], className: 'nx-tooltip' })
      }
      layersRef.current.push(marker)
      bounds.push([m.lat, m.lng])
    })

    // Circles — skip any with invalid coordinates
    circles.forEach(c => {
      if (!Number.isFinite(c.lat) || !Number.isFinite(c.lng) || !Number.isFinite(c.radius)) return
      const circle = L.circle([c.lat, c.lng], {
        radius: c.radius,
        color: c.color || COLORS.default,
        fillColor: c.fillColor || (c.color || COLORS.default),
        fillOpacity: 0.15,
        weight: 2,
        dashArray: '6 4',
      }).addTo(map)
      if (c.popup || c.label) {
        circle.bindPopup(`<div style="font-family:Raleway,sans-serif;font-size:13px"><b>${c.label || ''}</b>${c.popup ? '<br/>' + c.popup : ''}</div>`)
      }
      layersRef.current.push(circle)
      bounds.push([c.lat, c.lng])
      // Also push edge points for bounds
      const dlat = c.radius / 111000
      const dlng = c.radius / (111000 * Math.cos(c.lat * Math.PI / 180))
      bounds.push([c.lat + dlat, c.lng + dlng])
      bounds.push([c.lat - dlat, c.lng - dlng])
    })

    // Polygons — skip any with invalid coordinates
    polygons.forEach(p => {
      const latlngs = p.points
        .filter(pt => Number.isFinite(pt.lat) && Number.isFinite(pt.lng))
        .map(pt => [pt.lat, pt.lng] as [number, number])
      if (latlngs.length < 3) return
      const poly = L.polygon(latlngs, {
        color: p.color || COLORS.default,
        fillColor: p.fillColor || (p.color || COLORS.default),
        fillOpacity: 0.15,
        weight: 2,
        dashArray: '6 4',
      }).addTo(map)
      if (p.popup || p.label) {
        poly.bindPopup(`<div style="font-family:Raleway,sans-serif;font-size:13px"><b>${p.label || ''}</b>${p.popup ? '<br/>' + p.popup : ''}</div>`)
      }
      layersRef.current.push(poly)
      latlngs.forEach(ll => bounds.push(ll))
    })

    // Polyline (GPS trail)
    if (polyline && polyline.length > 1) {
      const latlngs = polyline
        .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng))
        .map(p => [p.lat, p.lng] as [number, number])
      if (latlngs.length < 2) return
      const line = L.polyline(latlngs, {
        color: polylineColor,
        weight: 3,
        opacity: 0.8,
        smoothFactor: 1,
      }).addTo(map)

      // Start / end markers
      const startIcon = L.divIcon({
        className: '',
        html: `<div style="width:12px;height:12px;border-radius:50%;background:#10b981;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)"></div>`,
        iconSize: [12, 12], iconAnchor: [6, 6],
      })
      const endIcon = L.divIcon({
        className: '',
        html: `<div style="width:12px;height:12px;border-radius:50%;background:#ef4444;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)"></div>`,
        iconSize: [12, 12], iconAnchor: [6, 6],
      })
      const sm = L.marker(latlngs[0], { icon: startIcon }).addTo(map).bindTooltip('Début', { direction: 'top' })
      const em = L.marker(latlngs[latlngs.length - 1], { icon: endIcon }).addTo(map).bindTooltip('Fin', { direction: 'top' })

      layersRef.current.push(line, sm, em)
      latlngs.forEach(ll => bounds.push(ll))
    }

    // Heatmap
    if (heatPoints.length > 0 && (L as any).heatLayer) {
      const validHeat = heatPoints.filter(h => Number.isFinite(h.lat) && Number.isFinite(h.lng))
      if (validHeat.length === 0) return
      const heat = (L as any).heatLayer(
        validHeat.map(h => [h.lat, h.lng, h.intensity ?? 0.5]),
        { radius: 25, blur: 15, maxZoom: 17, max: 1.0, gradient: { 0.4: 'var(--brand-orange)', 0.6: 'var(--brand-amber)', 0.8: '#f59e0b', 1: '#ef4444' } },
      ).addTo(map)
      layersRef.current.push(heat)
      validHeat.forEach(h => bounds.push([h.lat, h.lng]))
    }

    // Fit bounds — filter out invalid coords
    if (fitBounds && bounds.length > 0) {
      const validBounds = bounds.filter(b => Number.isFinite(b[0]) && Number.isFinite(b[1]))
      if (validBounds.length > 0) {
        try {
          map.fitBounds(validBounds, { padding: [40, 40], maxZoom: 16 })
        } catch {}
      }
    }
  }, [markers, circles, polygons, heatPoints, polyline, polylineColor, fitBounds, clearLayers])

  return (
    <div className={className} style={{ position: 'relative' }}>
      <div
        ref={containerRef}
        style={{
          height: typeof height === 'number' ? `${height}px` : height,
          width: '100%',
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid var(--bd, var(--bd))',
        }}
      />
      <style>{`
        .nx-tooltip {
          font-family: Raleway, sans-serif !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          padding: 4px 10px !important;
          border-radius: 6px !important;
          border: 1px solid var(--bd) !important;
          box-shadow: 0 2px 8px rgba(13,8,32,.12) !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 10px !important;
          box-shadow: 0 4px 16px rgba(13,8,32,.15) !important;
          border: 1px solid var(--bd) !important;
        }
        .leaflet-popup-tip { border-top-color: #fff !important; }
      `}</style>
    </div>
  )
}
